import { getAlgoliaResults } from "@algolia/autocomplete-js";
import { useI18n } from "vue-i18n";
import { useLanguages } from "@/core/composables";
import { prefixLocaleUrl } from "@/core/utilities";
import { addAlgoliaParametersToUrlFromSearchItem } from "@/shared/algolia-analytics/algolia-analytics";
import { getIndexName } from "@/shared/thorlabs/search";
import { buildExactMatchFilter } from "@/shared/thorlabs/search/utils/build-exact-match-filter";
import { getCdnUrl } from "@/shared/thorlabs/shared/image-resize";
import { debouncedFunction } from "./debounce";
import { searchClient } from "./searchClient";
import type { IAlgoliaProduct } from "../searchTypes";

export interface IProductMatchGroups {
  primary: IAlgoliaProduct[];
  additional: IAlgoliaProduct[];
}

/**
 * Splits the autocomplete product hits into the primary ("Item Matches") group and the
 * "Additional Matches" group shown for discontinued/superseded exact matches.
 * `topHits` is the main 15-hit result; `exactLookupHits` is the dedicated deterministic objectID
 * lookup used to surface an exact part-number match that the main query downranks past the first
 * 15 hits (e.g. a discontinued PM100D buried around #50).
 */
export function splitProductMatches(
  query: string,
  topHits: IAlgoliaProduct[],
  exactLookupHits: IAlgoliaProduct[] = [],
): IProductMatchGroups {
  const lowerQuery = query.toLowerCase().trim();
  const isExact = (hit: IAlgoliaProduct) => hit.objectID.toLowerCase() === lowerQuery;

  // Prefer an exact match already present in the main results; otherwise fall back to the
  // dedicated lookup so items buried past the first 15 hits still surface.
  const exactMatch = topHits.find(isExact) ?? exactLookupHits.find(isExact);

  if (!exactMatch) {
    return { primary: topHits.slice(0, 3), additional: [] };
  }

  exactMatch.exactMatch = true;
  const shouldHideOtherSources = exactMatch.itemStatus == "Superseded" || exactMatch.itemStatus == "Discontinued";

  if (exactMatch.itemStatus != "Active" && !shouldHideOtherSources) {
    return { primary: topHits.slice(0, 3), additional: [] };
  }

  if (shouldHideOtherSources) {
    exactMatch.hideOtherSources = true;
  }

  // Include the exact match even when it was found only via the deterministic lookup
  // (i.e. buried past the top hits).
  const baseHits = topHits.some(isExact) ? topHits : [exactMatch, ...topHits];

  // Keep the exact match first, then other items whose code starts with the query.
  const filteredAndSorted = baseHits
    .filter((item) => {
      const lowerCode = item.objectID.toLowerCase();
      return lowerCode === lowerQuery || lowerCode.startsWith(lowerQuery);
    })
    .sort((a, b) => {
      if (a.objectID.toLowerCase() === lowerQuery && b.objectID.toLowerCase() !== lowerQuery) {
        return -1;
      }
      if (b.objectID.toLowerCase() === lowerQuery && a.objectID.toLowerCase() !== lowerQuery) {
        return 1;
      }
      return 0;
    });

  if (!shouldHideOtherSources) {
    // Active exact match: everything stays under "Item Matches".
    return { primary: filteredAndSorted.slice(0, 3), additional: [] };
  }

  // The exact match's card already shows the item itself and (for superseded items) its
  // replacement SKU, so exclude both from the additional group to avoid rendering them twice.
  // Normalize (trim + lowercase) because ERP-sourced replacement IDs may carry stray whitespace.
  const normalizeId = (value: string | undefined) => value?.trim().toLowerCase() ?? "";
  const shownIds = new Set([normalizeId(exactMatch.objectID), normalizeId(exactMatch.replacementItemId)]);
  const additional = filteredAndSorted.filter((item) => !shownIds.has(normalizeId(item.objectID))).slice(0, 2);

  return { primary: [exactMatch], additional };
}

export function getProductsPlugin() {
  const { t } = useI18n();

  const { currentLocale, defaultLocale } = useLanguages();
  const productsIndexName = getIndexName("products", currentLocale.value);

  return {
    getSources({ query }: { query: string }) {
      if (!query) {
        return [];
      }

      const lowerQuery = query.toLowerCase().trim();

      const exactMatchFilter = buildExactMatchFilter(query);

      const buildQueries = () => [
        {
          indexName: productsIndexName,
          params: {
            query,
            hitsPerPage: 15,
            attributesToSnippet: ["name:10"],
            snippetEllipsisText: "…",
            clickAnalytics: true,
          },
        },
        {
          // Deterministic exact-match lookup. The main query above obeys the Algolia rule that
          // downranks discontinued/superseded items, so an exact part-number match (e.g. PM100D)
          // can fall outside the first 15 hits and never surface. Filtering by the unique objectID
          // returns exactly that record regardless of ranking or rules.
          indexName: productsIndexName,
          params: {
            query: "",
            filters: exactMatchFilter,
            hitsPerPage: 1,
            clickAnalytics: true,
          },
        },
      ];

      const renderCompactProduct = ({
        item,
        html,
      }: {
        html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string;
        item: IAlgoliaProduct;
      }) => {
        const itemClass =
          item.itemStatus === "Discontinued"
            ? "discontinued"
            : item.itemStatus === "Superseded"
              ? "discontinued superseded"
              : "";

        let itemUrl: string | undefined =
          item.itemStatus !== "Discontinued" && item.itemStatus !== "Superseded"
            ? `${item.familyPageUrl}?pn=${item.objectID}`
            : item.url;
        itemUrl = prefixLocaleUrl(itemUrl, currentLocale.value, defaultLocale.value);
        itemUrl = addAlgoliaParametersToUrlFromSearchItem(itemUrl, item);

        return html`<a
          href="${itemUrl}"
          class="product-match--item ${itemClass}"
          onClick=${() => {
            const event = new CustomEvent("closeSearchDropdown");
            document.dispatchEvent(event);
          }}
        >
          <div class="product-match--image">
            <img src="${getCdnUrl(item.imageUrl)}" width="80" height="80" alt="" role="presentation" />
          </div>
          <div class="product-match--content">
            <div class="flex flex-wrap gap-1 items-center">
              <div class="product-match--name">
                <span dangerouslySetInnerHTML=${{ __html: item.objectID }}></span>
              </div>
              ${item.itemStatus == "Discontinued" || item.itemStatus == "Superseded"
                ? html`<span class="discontinued-item--badge">${t("autocomplete.search.discontinued")}</span>`
                : ""}
            </div>
            <div class="product-match--info">
              <span dangerouslySetInnerHTML=${{ __html: item.name }}></span>
            </div>
          </div>
        </a>`;
      };

      return debouncedFunction([
        {
          sourceId: "products",
          getItems() {
            return getAlgoliaResults<IAlgoliaProduct>({
              searchClient: searchClient.value,
              queries: buildQueries(),
              transformResponse({ hits }) {
                return splitProductMatches(query, hits[0] ?? [], hits[1] ?? []).primary;
              },
            });
          },
          templates: {
            header({
              html,
              items,
            }: {
              html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string;
              items: IAlgoliaProduct[];
            }) {
              if (items.length === 3) {
                return html`<h6>${t("autocomplete.search.top_3_item_matches")}</h6>`;
              } else {
                return html`<h6>${t("autocomplete.search.top_item_matches")}</h6>`;
              }
            },
            item({
              item,
              html,
            }: {
              html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string;
              item: IAlgoliaProduct;
            }) {
              const itemClass =
                item.itemStatus === "Discontinued"
                  ? "discontinued"
                  : item.itemStatus === "Superseded"
                    ? "discontinued superseded"
                    : "";

              if (item.objectID.toLowerCase() == lowerQuery) {
                if (item.itemStatus == "Active") {
                  return html`
                    <a
                      href="${addAlgoliaParametersToUrlFromSearchItem(
                        prefixLocaleUrl(
                          `${item.familyPageUrl}?pn=${item.objectID}`,
                          currentLocale.value,
                          defaultLocale.value,
                        ),
                        item,
                      )}"
                      class="product-match--item ${itemClass}"
                      onClick=${() => {
                        const event = new CustomEvent("closeSearchDropdown");
                        document.dispatchEvent(event);
                      }}
                    >
                      <div class="product-match--image">
                        <img src="${getCdnUrl(item.imageUrl)}" width="80" height="80" alt="" role="presentation" />
                      </div>
                      <div class="product-match--content">
                        <div class="product-match--name">
                          <span dangerouslySetInnerHTML=${{ __html: item.objectID }}></span>
                          <div class="exact-match">
                            ${t("autocomplete.search.exact_match")}<span
                              ><svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M15.1499 3.35039C14.9249 3.12539 14.5749 3.12539 14.3499 3.35039L5.84994 11.6004L1.64994 7.47539C1.42494 7.25039 1.07494 7.27539 0.849942 7.47539C0.624942 7.70039 0.649942 8.05039 0.849942 8.27539L5.27494 12.5754C5.42494 12.7254 5.62494 12.8004 5.84994 12.8004C6.07494 12.8004 6.24994 12.7254 6.42494 12.5754L15.1499 4.10039C15.3749 3.92539 15.3749 3.57539 15.1499 3.35039Z"
                                  fill="#1A8245"
                                /></svg
                            ></span>
                          </div>
                        </div>
                        <div class="product-match--info">
                          <span dangerouslySetInnerHTML=${{ __html: item.name }}></span>
                        </div>
                        ${item.softwareDownloadPageUrl
                          ? html` <a
                              href="${prefixLocaleUrl(
                                item.softwareDownloadPageUrl,
                                currentLocale.value,
                                defaultLocale.value,
                              )}"
                              class="product-match--link"
                              >${t("autocomplete.search.software_download_page")}
                              <span>
                                <svg class="vc-icon shrink-0">
                                  <use
                                    href="/static/thorlabs/icons/basic/icon-arrow-up-right.svg?#icon"
                                  ></use></svg></span
                            ></a>`
                          : ``}
                      </div>
                    </a>
                  `;
                } else if (item.itemStatus == "Superseded" && item.replacementItemId) {
                  return html` <div class="discontinued-group ${itemClass}">
                    <div class="discontinued-item--status">
                      ${item.objectID} ${t("autocomplete.search.discontinued_item_replaced_by")}
                      <a
                        href="${prefixLocaleUrl(
                          `${item.replacementItemFamilyPageUrl}?pn=${item.replacementItemId}`,
                          currentLocale.value,
                          defaultLocale.value,
                        )}"
                        target="_blank"
                        onClick=${() => {
                          const event = new CustomEvent("closeSearchDropdown");
                          document.dispatchEvent(event);
                        }}
                        >${item.replacementItemId}</a
                      >.
                    </div>
                    <div class="discontinued-group--wrapper">
                      <a
                        href="${addAlgoliaParametersToUrlFromSearchItem(
                          prefixLocaleUrl(item.url, currentLocale.value, defaultLocale.value),
                          item,
                        )}"
                        class="discontinued-item"
                        onClick=${() => {
                          const event = new CustomEvent("closeSearchDropdown");
                          document.dispatchEvent(event);
                        }}
                      >
                        <div class="discontinued-item--wrapper">
                          <div class="discontinued-item--info">
                            <span class="discontinued-item--name">${item.objectID}</span>
                            <span class="discontinued-item--badge">${t("autocomplete.search.discontinued")}</span>
                            <div class="exact-match">
                              ${t("autocomplete.search.exact_match")}<span
                                ><svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M15.1499 3.35039C14.9249 3.12539 14.5749 3.12539 14.3499 3.35039L5.84994 11.6004L1.64994 7.47539C1.42494 7.25039 1.07494 7.27539 0.849942 7.47539C0.624942 7.70039 0.649942 8.05039 0.849942 8.27539L5.27494 12.5754C5.42494 12.7254 5.62494 12.8004 5.84994 12.8004C6.07494 12.8004 6.24994 12.7254 6.42494 12.5754L15.1499 4.10039C15.3749 3.92539 15.3749 3.57539 15.1499 3.35039Z"
                                    fill="#1A8245"
                                  /></svg
                              ></span>
                            </div>
                          </div>
                          <div class="discontinued-item--details">
                            <span dangerouslySetInnerHTML=${{ __html: item.name }}></span>
                          </div>
                        </div>
                        <div class="discontinued-item--image">
                          <img src="${getCdnUrl(item.imageUrl)}" width="268" height="128" alt="" role="presentation" />
                        </div>
                      </a>

                      <a
                        href="${prefixLocaleUrl(
                          `${item.replacementItemFamilyPageUrl}?pn=${item.replacementItemId}`,
                          currentLocale.value,
                          defaultLocale.value,
                        )}"
                        class="discontinued-item discontinued-item--replacement"
                        onClick=${() => {
                          const event = new CustomEvent("closeSearchDropdown");
                          document.dispatchEvent(event);
                        }}
                      >
                        <div class="discontinued-item--wrapper">
                          <div class="discontinued-item--name">
                            <span dangerouslySetInnerHTML=${{ __html: item.replacementItemId }}></span>
                          </div>
                          <div class="discontinued-item--details">
                            <span dangerouslySetInnerHTML=${{ __html: item.replacementItemName }}></span>
                          </div>
                        </div>
                        <div class="discontinued-item--image">
                          <img
                            src="${item.replacementItemImageUrl}"
                            width="268"
                            height="128"
                            alt=""
                            role="presentation"
                          />
                        </div>
                      </a>
                    </div>
                  </div>`;
                } else if (
                  item.itemStatus == "Discontinued" ||
                  (item.itemStatus == "Superseded" && !item.replacementItemId)
                ) {
                  return html` <div class="discontinued-group ${itemClass}">
                    <div class="discontinued-item--status">
                      ${item.objectID} ${t("autocomplete.search.has_been_discontinued")}
                    </div>
                    <a
                      href="${addAlgoliaParametersToUrlFromSearchItem(
                        prefixLocaleUrl(item.url, currentLocale.value, defaultLocale.value),
                        item,
                      )}"
                      class="discontinued-item"
                      onClick=${() => {
                        const event = new CustomEvent("closeSearchDropdown");
                        document.dispatchEvent(event);
                      }}
                    >
                      <div class="discontinued-item--wrapper">
                        <div class="discontinued-item--info">
                          <span class="discontinued-item--name">
                            <span dangerouslySetInnerHTML=${{ __html: item.objectID }}></span>
                          </span>
                          <span class="discontinued-item--badge">${t("autocomplete.search.discontinued")}</span>
                          <div class="exact-match">
                            ${t("autocomplete.search.exact_match")}<span
                              ><svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M15.1499 3.35039C14.9249 3.12539 14.5749 3.12539 14.3499 3.35039L5.84994 11.6004L1.64994 7.47539C1.42494 7.25039 1.07494 7.27539 0.849942 7.47539C0.624942 7.70039 0.649942 8.05039 0.849942 8.27539L5.27494 12.5754C5.42494 12.7254 5.62494 12.8004 5.84994 12.8004C6.07494 12.8004 6.24994 12.7254 6.42494 12.5754L15.1499 4.10039C15.3749 3.92539 15.3749 3.57539 15.1499 3.35039Z"
                                  fill="#1A8245"
                                /></svg
                            ></span>
                          </div>
                        </div>
                        <div class="discontinued-item--details">
                          <span dangerouslySetInnerHTML=${{ __html: item.name }}></span>
                        </div>
                      </div>
                      <div class="discontinued-item--image">
                        <img src="${getCdnUrl(item.imageUrl)}" width="268" height="128" alt="" role="presentation" />
                      </div>
                    </a>
                  </div>`;
                }
              }
              return renderCompactProduct({ item, html });
            },
            noResults() {
              return t("autocomplete.search.empty_products");
            },
          },
        },
        {
          sourceId: "productsAdditional",
          getItems() {
            return getAlgoliaResults<IAlgoliaProduct>({
              searchClient: searchClient.value,
              queries: buildQueries(),
              transformResponse({ hits }) {
                return splitProductMatches(query, hits[0] ?? [], hits[1] ?? []).additional;
              },
            });
          },
          templates: {
            header({ html }: { html: (literals: TemplateStringsArray, ...placeholders: unknown[]) => string }) {
              return html`<h6>${t("autocomplete.search.additional_matches")}</h6>`;
            },
            item: renderCompactProduct,
          },
        },
      ]);
    },
  };
}
